import { QrCodeScanner } from "@mui/icons-material";
import { Button, Modal, Typography } from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import InProgress from "./InProgress";

export default function MallScanner({
  getValue,
  onCreate,
  getIndividualValue,
  individualValue,
  value,
  code,
  extendState,
  full,
  onScanComplete,
  label = "Bắt đầu scan",
  fullLabel = "Hoàn thành scan mã",
  hideEnter = false,
  disabledProcess = false,
  autoStartScan = false,
  onScanStopped,
  ...props
}) {
  const [scanning, setScanning] = useState(false);
  const [scanningCompleted, setScanningCompleted] = useState(false);
  const [touchMove, setTouchMove] = useState(false);
  const buffer = useRef("");
  const lastTime = useRef(Date.now());
  const SCAN_THRESHOLD = 50;
  const valueRef = useRef(value);
  const codesKey = code ? code : "itemCodes";

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    valueRef.current = value;

    const itemCodes = value?.[codesKey];

    if (!value || (Array.isArray(itemCodes) && itemCodes.length === 0)) {
      setScanning(false);
      setScanningCompleted(false);
      setTouchMove(false);
      buffer.current = "";
    }
  }, [value, codesKey]);

  const stopScan = useCallback(() => {
    buffer.current = "";
    lastTime.current = Date.now();
    setScanning(false);
    setTouchMove(false);
    onScanStopped?.();
  }, [onScanStopped]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!scanning) return;

      const currentTime = Date.now();
      const diff = currentTime - lastTime.current;

      if (diff <= SCAN_THRESHOLD) e.preventDefault();
      if (diff > SCAN_THRESHOLD) buffer.current = "";

      lastTime.current = currentTime;

      const toArray = (v) => (Array.isArray(v) ? v : []);

      if (e.key === "Enter") {
        e.preventDefault();

        if (buffer.current.length > 0) {
          if (getIndividualValue) {
            getIndividualValue(buffer.current);
          } else {
            const itemCodes = toArray(valueRef.current?.[codesKey]);

            getValue({
              ...valueRef.current,
              ...extendState,
              [codesKey]: [...itemCodes, buffer.current],
            });
          }
          buffer.current = "";
          return;
        }

        stopScan();
        return;
      }

      if (e.key.length === 1) {
        buffer.current += e.key;
      }
    },
    [scanning, getIndividualValue, individualValue, extendState, codesKey, getValue, stopScan],
  );

  useEffect(() => {
    const listener = (e) => handleKeyDown(e);
    if (scanning) window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [scanning, handleKeyDown]);

  const toggleScan = () => {
    if (props.disabled || full) return; // ✅ only added `full`
    buffer.current = "";
    lastTime.current = Date.now();
    setScanning((prev) => !prev);
  };

  useEffect(() => {
    if (!autoStartScan || props.disabled || full) return;
    buffer.current = "";
    lastTime.current = Date.now();
    setScanning(true);
  }, [autoStartScan, props.disabled, full]);

  useEffect(() => {
    if (scanning) {
      if (full) {
        setScanningCompleted(true);
        const timer = setTimeout(() => {
          setScanningCompleted(false);
          onScanComplete?.();
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [scanning, full, onScanComplete]);

  const handleMouseOver = () => {
    if (scanning) {
      setTouchMove(true);
    }
  };

  const handleMouseLeave = () => {
    if (scanning) {
      setTouchMove(false);
    }
  };

  return (
    <>
      <Button
        disabled={props.disabled || full}
        onClick={() => {
          toggleScan();
          if (onCreate) onCreate();
        }}
        size={props?.size || "small"}
        sx={{
          width: "100%",
          border: "0",
          borderRadius: "4px",
          background: props.disabled || full ? "var(--grey)" : "var(--black)",
          color: props.disabled || full ? "var(--dark-grey)" : "var(--white)",
          cursor: props.disabled || full ? "not-allowed" : "pointer",
          opacity: props.disabled || full ? 0.6 : 1,
          ...props?.style,
        }}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        startIcon={<QrCodeScanner />}
        {...props}
      >
        {full ? fullLabel : scanning ? (touchMove ? "Dừng quá trình scan" : "Tiếp tục scan...") : label}
      </Button>

      {scanning && !hideEnter && (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="error"
        >
          (Nhấn Enter để dừng scan)
        </Typography>
      )}
      <Modal
        open={scanningCompleted && !disabledProcess}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <InProgress />
      </Modal>
    </>
  );
}
